import React, { useState } from "react"
import axios from "axios"
import { Button, Col, Input, Modal, ModalBody, Row, Spinner } from "reactstrap"
import { Search } from "lucide-react"
import { api } from "../../utils/variables"
import { usePlatforms } from "../../contexts/PlatformsContext"
import { POLL_INTERVAL_MS } from "../../config/syncConfig"
import { buildKeywordSearchParams, KEYWORD_SEARCH_PATH, normalizeSearchGames } from "../../utils/keywordSearch"

// Searches across every active platform in parallel, passing `platform` as a
// query param per request (same pattern as GlobalSearchBar / AppScanner),
// since the /tracker/games/search endpoint scopes results to one platform per call.
const ReportAppSearchModal = ({ isOpen, toggle, onAppSelect }) => {
  const { getActivePlatforms, getPlatformDisplayName, getPlatformIcon } = usePlatforms()
  const [inputValue, setInputValue] = useState("")
  const [searchTerm, setSearchTerm] = useState("")
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)

  const isUpgradeResponse = data => {
    if (!data || typeof data !== "object") return false
    return data?.status === 400 && (data?.action || "") === "UPGRADE" && !!data?.current_tier
  }

  const fetchPlatformResults = async (platformObj, term) => {
    const platform = platformObj.id
    try {
      const params = new URLSearchParams(buildKeywordSearchParams(term, { page: 1, platform }))
      const url = `${api}${KEYWORD_SEARCH_PATH}?${params.toString()}`

      let response = await axios.get(url)
      if (isUpgradeResponse(response.data)) return { upgrade: true }

      let action = (response.data?.action || "").toLowerCase()
      let attempts = 0
      const maxAttempts = 12
      const pollIntervalMs = POLL_INTERVAL_MS || 5000

      while (action === "loading" && attempts < maxAttempts) {
        attempts++
        await new Promise(r => setTimeout(r, pollIntervalMs))
        response = await axios.get(url)
        if (isUpgradeResponse(response.data)) return { upgrade: true }
        action = (response.data?.action || "").toLowerCase()
      }

      const games = normalizeSearchGames(response.data?.games || [])
      const platformName = getPlatformDisplayName(platform) || platformObj.name || platform
      const platformIconUrl = getPlatformIcon ? getPlatformIcon(platform) : null

      return {
        games: games.map(game => ({
          ...game,
          platform: game.platform || platformName,
          platformIcon: platformIconUrl,
        })),
      }
    } catch (error) {
      console.error(`Search failed for ${platformObj.id}:`, error)
      return { games: [] }
    }
  }

  const handleSearch = async () => {
    const term = inputValue.trim()
    if (!term) return
    setSearchTerm(term)
    setLoading(true)
    try {
      const activePlatforms = getActivePlatforms()
      const platformResults = await Promise.all(activePlatforms.map(p => fetchPlatformResults(p, term)))
      const allGames = platformResults.flatMap(result => result.games || [])
      setResults(allGames)
    } catch (error) {
      console.error("Report app search error", error)
      setResults([])
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg" className="report-app-search-modal">
      <div className="modal-header">
        <h5 className="modal-title">Select an App</h5>
        <button type="button" className="btn-close" onClick={toggle} aria-label="Close"></button>
      </div>
      <ModalBody style={{ maxHeight: "80vh", minHeight: "40vh", padding: 24, overflowY: "auto", background: "#1a1b24" }}>
        <Row>
          <Col>
            <div className="d-flex flex-wrap gap-3">
              <div className="search-box position-relative flex-grow-1">
                <Input
                  type="text"
                  className="form-control"
                  style={{
                    background: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: 12,
                    color: "#fff",
                    padding: "12px 16px 12px 40px",
                  }}
                  placeholder="Search apps by name or paste a URL..."
                  value={inputValue}
                  onChange={e => setInputValue(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleSearch()}
                />
                <Search size={18} color="rgba(255, 255, 255, 0.4)" style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)" }} />
              </div>
              <Button
                style={{ background: "#F6255A", border: "none", borderRadius: 12, padding: "0 24px", fontWeight: 600 }}
                onClick={handleSearch}
              >
                Search
              </Button>
            </div>
          </Col>
        </Row>

        <div style={{ marginTop: 24 }}>
          {results.length === 0 ? (
            <div className="text-center py-5" style={{ background: "rgba(255, 255, 255, 0.02)", borderRadius: 16, border: "1px dashed rgba(255, 255, 255, 0.1)" }}>
              <h6 style={{ color: "#fff", fontWeight: 600, marginBottom: 8 }}>
                {loading ? "Searching..." : searchTerm ? "No Results Found" : "Search Apps & Games"}
              </h6>
              <p style={{ color: "rgba(255, 255, 255, 0.5)", fontSize: 13, maxWidth: 280, margin: "0 auto" }}>
                {searchTerm ? `We couldn't find any results for "${searchTerm}"` : "Type a name above to find the app you want a report for."}
              </p>
            </div>
          ) : (
            results.map((game, index) => (
              <Row className="pb-2" key={game.item_id || game._id || index}>
                <Col lg={12}>
                  <div
                    style={{
                      background: "rgba(255, 255, 255, 0.03)",
                      border: "1px solid rgba(255, 255, 255, 0.05)",
                      borderRadius: 16,
                      overflow: "hidden",
                      display: "flex",
                      alignItems: "center",
                      padding: "8px 12px",
                    }}
                  >
                    <img
                      src={game?.image_url || "https://via.placeholder.com/50"}
                      alt={game?.name}
                      style={{ height: 56, width: 56, objectFit: "contain", borderRadius: 12, background: "rgba(0,0,0,0.2)", marginRight: 12 }}
                    />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <b className="text-white d-block text-truncate" style={{ fontSize: 15 }}>{game?.name || "App Name"}</b>
                      <span style={{ color: "rgba(255, 255, 255, 0.4)", fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
                        {game.platformIcon ? (
                          <img src={game.platformIcon} alt={game.platform} style={{ width: 14, height: 14, opacity: 0.8 }} />
                        ) : null}
                        {game?.platform ? getPlatformDisplayName(game.platform) || game.platform : "Unknown"}
                      </span>
                    </div>
                    <Button
                      size="sm"
                      style={{ background: "rgba(246, 37, 90, 0.1)", color: "#F6255A", border: "1px solid rgba(246, 37, 90, 0.2)", borderRadius: 8, padding: "6px 16px", fontWeight: 600 }}
                      onClick={() => onAppSelect(game)}
                    >
                      Select
                    </Button>
                  </div>
                </Col>
              </Row>
            ))
          )}
        </div>

        {loading && (
          <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "rgba(0, 0, 0, 0.7)", zIndex: 9999 }}>
            <Spinner color="primary" />
          </div>
        )}
      </ModalBody>
      <style>{`
        .report-app-search-modal .modal-content {
          background: #1a1b24 !important;
          border: 1px solid rgba(255, 255, 255, 0.1) !important;
          border-radius: 20px !important;
          overflow: hidden;
        }
        .report-app-search-modal .modal-header {
          border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
          padding: 20px 24px !important;
          background: #1a1b24 !important;
        }
        .report-app-search-modal .modal-header .modal-title {
          color: #fff !important;
          font-weight: 600 !important;
          font-size: 18px !important;
        }
        .report-app-search-modal .btn-close {
          filter: invert(1) !important;
          opacity: 0.6 !important;
        }
        .report-app-search-modal .btn-close:hover {
          opacity: 1 !important;
        }
      `}</style>
    </Modal>
  )
}

export default ReportAppSearchModal
